import { ref, watch, type Ref } from 'vue'

/**
 * The host page's `sendMessage()`, safe to call at any time. The chat takes a
 * message only once it is mounted and the conversation it shows is loaded:
 * before that (a floating panel just opened, the session or the agent still
 * loading, a new conversation starting) the text waits here and goes out on
 * the chat's `loaded` for the current conversation. A load of a conversation
 * the chat has already left is ignored: a message sent before the history
 * arrives would hide that history, because the store does not overwrite a
 * conversation that already has messages. A later call replaces a text
 * still waiting, and blank text is ignored.
 */
export function usePendingMessage(
    chat: Readonly<
        Ref<{ sendMessage: (text: string) => void } | null | undefined>
    >,
    /** The conversation the chat shows now. */
    currentChatId: () => string | undefined,
) {
    const pending = ref<string>()
    /** The conversation whose history the mounted chat has loaded. */
    const loadedChatId = ref<string>()

    const flush = () => {
        const text = pending.value
        if (
            text === undefined ||
            !chat.value ||
            loadedChatId.value === undefined ||
            loadedChatId.value !== currentChatId()
        ) {
            return
        }
        pending.value = undefined
        chat.value.sendMessage(text)
    }

    watch(chat, (current) => {
        if (!current) {
            loadedChatId.value = undefined
        }
    })

    return {
        send(text: string) {
            if (!text.trim()) {
                return
            }
            pending.value = text
            flush()
        },
        /** Bound to the chat's `loaded` event. */
        onLoaded(chatId: string) {
            if (chatId !== currentChatId()) {
                return
            }
            loadedChatId.value = chatId
            flush()
        },
        /**
         * A new conversation is starting: until it has loaded, a message
         * waits instead of landing in the one being left.
         */
        invalidate() {
            loadedChatId.value = undefined
        },
        /** The user closed the chat: a message still waiting is dropped. */
        clear() {
            pending.value = undefined
        },
    }
}
